/**
 * DataSource transport contract.
 *
 * Every screen, IPC call, and push-event subscription in @cipherops/ui
 * goes through this interface. Two implementations exist:
 *
 *   - LocalDataSource   — wraps window.cipherops (Electron preload bridge)
 *   - RemoteDataSource  — wraps fetch() + WebSocket (web shell)
 *
 * Both speak the same protocol: channel names + payloads + push events.
 * The shell decides which implementation to install at startup.
 *
 * The interface is deliberately minimal — two methods. We do NOT enumerate
 * every IPC channel as a typed method here, because:
 *   1. There are 60+ channels and they change with feature work
 *   2. A typed surface would couple every shell rebuild to every channel rename
 *   3. Channel-string + payload is what the IPC bridge already speaks; the
 *      schema validation lives in app/electron/services/_ipc-schemas.ts
 *
 * Per-channel typing happens at the call site in screens, where the developer
 * already knows what channel they're invoking and what shape comes back.
 */

export class IpcError extends Error {
  code: string;
  details?: unknown;
  /**
   * The X-Request-Id correlated with this failure, if the transport
   * managed to capture one. May be undefined for pre-fetch failures
   * (e.g. SESSION_REQUIRED before the request was sent) or for transports
   * that don't surface request ids (e.g. LocalDataSource).
   *
   * Surface this to the user in bug-report flows so server logs can be
   * grepped end-to-end. See apps/server/REQUEST-ID.md.
   */
  requestId?: string;
  constructor(message: string, code: string, details?: unknown, requestId?: string) {
    super(message);
    this.name = 'IpcError';
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

export interface DataSource {
  /**
   * Invoke a server-side handler by channel name.
   * Throws IpcError on any handler error (with .code, .message, .details).
   * Returns the typed response shape.
   */
  invoke<T = unknown>(channel: string, payload?: unknown): Promise<T>;

  /**
   * Subscribe to a server-pushed event stream (auto-lock signals,
   * agent pipeline progress, system status, etc.).
   * Returns an unsubscribe function. Always safe to call multiple times.
   */
  onPush(event: string, handler: (payload: unknown) => void): () => void;
}
